import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Star, Phone, ChevronRight, X } from "lucide-react";
import { BRAND } from "../data/siteData";
import { LeadForm } from "./LeadForm";

export const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

export const SectionLabel = ({ children }) => (
  <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#E8B86D] mb-4">{children}</p>
);

export const Stars = ({ count = 5, size = 16 }) => (
  <div className="flex gap-0.5" aria-label={`${count} star rating`}>
    {Array.from({ length: count }).map((_, i) => (
      <Star key={i} size={size} className="text-[#E8B86D] fill-[#E8B86D]" />
    ))}
  </div>
);

export const Breadcrumbs = ({ items }) => (
  <nav className="flex items-center flex-wrap gap-1.5 text-xs text-gray-400 mb-6" aria-label="Breadcrumb" data-testid="breadcrumbs">
    {items.map((it, i) => (
      <span key={it.path} className="flex items-center gap-1.5">
        {i > 0 && <ChevronRight size={12} />}
        {i === items.length - 1 ? (
          <span className="text-[#028090] font-medium">{it.name}</span>
        ) : (
          <Link to={it.path} className="hover:text-[#028090] transition-colors">{it.name}</Link>
        )}
      </span>
    ))}
  </nav>
);

export const BeforeAfterSlider = ({ before, after, altBefore = "bare concrete garage floor before coating", altAfter = "polyaspartic garage floor coating after install", className = "" }) => {
  const [pos, setPos] = useState(50);
  return (
    <div className={`relative w-full overflow-hidden select-none aspect-[4/3] ${className}`} data-testid="before-after-slider">
      <img src={after} alt={altAfter} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <img src={before} alt={altBefore} loading="lazy" className="absolute inset-0 w-full h-full object-cover grayscale" />
      </div>
      <div className="absolute top-0 bottom-0 w-0.5 bg-white" style={{ left: `${pos}%` }}>
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-9 h-9 bg-[#028090] border-2 border-white flex items-center justify-center text-white text-xs font-bold rounded-full shadow-lg">⇄</div>
      </div>
      <input
        type="range" min="2" max="98" value={pos}
        onChange={(e) => setPos(Number(e.target.value))}
        className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize"
        aria-label="Before and after comparison slider"
        data-testid="before-after-range"
      />
      <span className="absolute top-3 left-3 bg-black/60 text-white text-[10px] uppercase tracking-widest px-2.5 py-1 pointer-events-none">Before</span>
      <span className="absolute top-3 right-3 bg-[#028090] text-white text-[10px] uppercase tracking-widest px-2.5 py-1 pointer-events-none">After</span>
    </div>
  );
};

export const FlakeSwatch = ({ flake, size = "h-32" }) => {
  const dots = [];
  let seed = flake.name.length * 7;
  const rand = () => { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; };
  for (let i = 0; i < 55; i++) {
    dots.push({
      left: rand() * 96, top: rand() * 92,
      w: 2 + rand() * 5, h: 2 + rand() * 4,
      color: flake.chips[i % flake.chips.length],
      rot: rand() * 90,
    });
  }
  return (
    <div className={`relative w-full ${size} overflow-hidden`} style={{ backgroundColor: flake.base }} data-testid={`flake-swatch-${flake.name.toLowerCase().replace(/\s/g, "-")}`}>
      {dots.map((d, i) => (
        <span key={i} className="absolute" style={{ left: `${d.left}%`, top: `${d.top}%`, width: d.w, height: d.h, backgroundColor: d.color, transform: `rotate(${d.rot}deg)` }} />
      ))}
    </div>
  );
};

export const CTASection = ({ title = "Get Your Free Garage Quote", subtitle = "Free remote estimate using satellite imagery. Flat-rate written pricing. One-day install.", showForm = false }) => (
  <section className="relative bg-[#000000] py-24 md:py-32 px-6 md:px-12 overflow-hidden grain">
    <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 60% 50% at 50% 100%, rgba(2,128,144,0.25) 0%, transparent 70%)" }} />
    <div className="relative max-w-4xl mx-auto text-center">
      <SectionLabel>Ready When You Are</SectionLabel>
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-5">{title}</h2>
      <p className="text-gray-300 text-base md:text-lg mb-10 max-w-2xl mx-auto">{subtitle}</p>
      {showForm ? (
        <div className="max-w-2xl mx-auto text-left"><LeadForm dark /></div>
      ) : (
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/free-estimate" data-testid="cta-section-estimate-btn" className="bg-[#028090] text-white hover:bg-[#026c7a] transition-colors duration-300 px-8 py-4 uppercase font-bold tracking-wider">
            Get My Free Garage Quote
          </Link>
          <a href={BRAND.phoneHref} data-testid="cta-section-phone-btn" className="border border-white/30 text-white hover:border-[#028090] hover:text-[#028090] transition-colors duration-300 px-8 py-4 uppercase font-bold tracking-wider flex items-center justify-center gap-2">
            <Phone size={18} strokeWidth={1.5} /> {BRAND.phone}
          </a>
        </div>
      )}
    </div>
  </section>
);

export const ExitIntentPopup = () => {
  const [show, setShow] = useState(false);
  const fired = useRef(false);

  useEffect(() => {
    if (sessionStorage.getItem("vx_popup_shown")) return;
    const trigger = () => {
      if (fired.current) return;
      fired.current = true;
      sessionStorage.setItem("vx_popup_shown", "1");
      setShow(true);
    };
    const timer = setTimeout(trigger, 30000);
    const onMouseLeave = (e) => { if (e.clientY <= 0) trigger(); };
    document.addEventListener("mouseleave", onMouseLeave);
    return () => { clearTimeout(timer); document.removeEventListener("mouseleave", onMouseLeave); };
  }, []);

  if (!show) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="exit-intent-popup">
      <div className="relative bg-[#000000] border border-white/15 max-w-lg w-full p-8 md:p-10 teal-glow max-h-[90vh] overflow-y-auto">
        <button onClick={() => setShow(false)} data-testid="exit-popup-close" aria-label="Close" className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors">
          <X size={22} />
        </button>
        <SectionLabel>Before You Go</SectionLabel>
        <h3 className="font-display text-2xl md:text-3xl font-bold text-white mb-3">Lock In Your Free Garage Quote</h3>
        <p className="text-gray-400 text-sm mb-6">Free remote estimate · One-day install · Lifetime warranty.</p>
        <LeadForm dark compact sourceTag="exit-popup" />
      </div>
    </div>
  );
};
